import { useEffect, useRef, useState } from 'react';
import { callLogControlId, type CallEntry } from '@/stores/voip.store';

/**
 * The server-side call-control id, polled until it exists.
 *
 * The caller's leg when the call carries one — see `callLogControlId`. The
 * transcript stream and the access check resolve either leg, but the intake is
 * stored under exactly the id pushed here, and the job form reads it back under
 * the caller leg.
 *
 * `telnyxIDs` lives on the MUTABLE Telnyx SDK object, outside reactive state —
 * on outbound calls it can populate after the render that mounted this panel,
 * and nothing re-renders when it does. Reading it once at render time is the
 * bug that made the panel sit silent with no request at all. Poll every 500ms
 * until the id shows up (stops the moment it does, or on unmount).
 *
 * Keyed by `call.id`, NOT the entry object: the store rebuilds a call entry on
 * every state change (mute, hold, stream swap), so depending on the object
 * restarted this effect mid-call — resetting a resolved id back to null and
 * tearing the SSE stream down with it. The live entry is read through a ref so
 * the poll still sees the mutable SDK fields without re-subscribing.
 */
export function useResolvedCallControlId(call: CallEntry | null): string | null {
  const callRef = useRef<CallEntry | null>(call);
  callRef.current = call;

  const [id, setId] = useState<string | null>(() => callLogControlId(call) ?? null);

  // A resolved id belongs to the call it was resolved FOR, and to no other.
  //
  // Clearing it in the effect would leave one render — and on an outbound call
  // up to a second and a half of polling, or the whole grace period before the
  // `active~` fallback — where this hook still hands back the PREVIOUS call's
  // id under the new caller. Anything keyed on it, the transcript stream and
  // the intake read, would spend that window showing one customer's
  // conversation beside another's. So the swap is handled during render, the
  // way React documents adjusting state when a prop changes: by the time
  // anything reads `id`, it is already this call's own or null.
  const [idOwner, setIdOwner] = useState<string | null>(call?.id ?? null);
  if ((call?.id ?? null) !== idOwner) {
    setIdOwner(call?.id ?? null);
    setId(callLogControlId(call) ?? null);
  }

  useEffect(() => {
    if (!callRef.current) return;
    const initial = callLogControlId(callRef.current) ?? null;
    if (initial) {
      setId(initial);
      return;
    }

    let polls = 0;
    const timer = setInterval(() => {
      polls += 1;
      const current = callRef.current;
      if (!current) return;
      const found = callLogControlId(current) ?? null;
      if (found) {
        setId(found);
        clearInterval(timer);
        return;
      }
      // The SDK never exposes the id for calls the browser PLACES (verified
      // live: outbound calls poll forever). After a short grace period, ask
      // the server for "my active outbound call" via the `active` alias. The
      // dialed number rides inside the alias as a hint: browser-dialed 2.0
      // calls present the tenant's DID as `from`, so the server cannot know
      // WHICH user placed the call — but it does know which number each
      // outbound call dialed, and so do we. Resolution stays inside the
      // token's own tenant either way.
      if (current.direction === 'outbound' && polls >= 3) {
        const digits = (current.peerNumber ?? '').replace(/\D/g, '');
        setId(digits.length >= 4 ? `active~${digits}` : 'active');
        clearInterval(timer);
      }
    }, 500);
    return () => clearInterval(timer);
  }, [call?.id]);

  return id;
}

/**
 * Who is speaking, by name where we have one.
 *
 * "Caller" is what you call someone you do not know. On a call from a customer
 * on file — or one who has given their name — reading their actual name back
 * in the transcript is the difference between a log and a conversation.
 */
